'use client';
/**
 * Live progress readout for a running export job. Shared by the export dialog and (via the
 * status helpers) by the export history table and the admin dashboard.
 */
import * as React from 'react';
import { useTranslations } from 'next-intl';
import type { ExportJobDto, ExportStatus } from '@freealbum/shared';
import { ProgressBar, type BadgeVariant } from '@freealbum/ui';

/** The stage names we have copy for, under `export.stage.*`. */
export type ExportStageKey = 'queued' | 'preparing' | 'derivatives' | 'rendering' | 'merging' | 'flipbook' | 'uploading' | 'done';

/**
 * `job.stage` is a free-form string produced by the worker (e.g. "render:derivatives 12/40"),
 * so we match on substrings rather than on an enum. Order matters: the more specific tokens
 * come first ("derivatives" would otherwise be swallowed by a "rendering derivatives" message).
 */
const STAGE_MATCHERS: readonly (readonly [token: string, key: ExportStageKey])[] = [
  ['queue', 'queued'],
  ['prepar', 'preparing'],
  ['derivativ', 'derivatives'],
  ['flipbook', 'flipbook'],
  ['merg', 'merging'],
  ['upload', 'uploading'],
  ['render', 'rendering'],
  ['done', 'done'],
];

/** When the stage is missing or unrecognised we fall back to something derived from the status. */
const STATUS_FALLBACK_STAGE: Record<ExportStatus, ExportStageKey> = {
  queued: 'queued',
  rendering: 'rendering',
  done: 'done',
  failed: 'rendering',
  cancelled: 'queued',
};

export function stageKey(stage: string | null | undefined, status: ExportStatus): ExportStageKey {
  const needle = (stage ?? '').toLowerCase();
  if (needle) {
    for (const [token, key] of STAGE_MATCHERS) if (needle.includes(token)) return key;
  }
  return STATUS_FALLBACK_STAGE[status];
}

/** Badge tone per export status — shared so history and admin stay visually consistent. */
export const EXPORT_STATUS_TONE: Record<ExportStatus, BadgeVariant> = {
  queued: 'neutral',
  rendering: 'info',
  done: 'success',
  failed: 'danger',
  cancelled: 'warning',
};

export const clampProgress = (v: number) => Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));

export function ExportProgress({ job }: { job: ExportJobDto }): React.JSX.Element {
  const t = useTranslations('export');
  const progress = clampProgress(job.progress);
  const percent = Math.round(progress * 100);
  const key = stageKey(job.stage, job.status);
  const stageText = t(`stage.${key}`);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-ink-2">{t('progressLead')}</p>
      <p role="status" aria-live="polite" aria-atomic="true" className="text-sm text-ink">{stageText}</p>
      <ProgressBar value={progress * 100} label={t('progressTitle')} hint={t('percent', { percent })} />
    </div>
  );
}
